import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuroraBackground from '../components/AuroraBackground/AuroraBackground';
import LanguageSwitcher from '../components/LanguageSwitcher/LanguageSwitcher';
import MetaHead from '../components/MetaHead';
import { API_BASE } from '../config/api';

// ⚠️ Logique fonctionnelle 100% préservée (i18n P4 — pas de changement de comportement) :
//  - handleSubmit : POST /api/auth/forgot-password, gestion 429 + 5xx
//  - État `sent` → message générique anti-énumération
//  - Submit Enter, lien "← Retour à la connexion"
// Ajout P4 : envoi de `lang: i18n.language` au backend pour email reset localisé.

export default function ForgotPassword() {
  const { t, i18n } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          // i18n P4 : transmet la langue active au backend pour localiser l'email
          // de reset. Le backend whitelist (['fr','en','es','it']) avec fallback FR.
          lang: i18n.language || 'fr'
        })
      });
      // Le backend renvoie toujours 200 avec un message générique pour ne pas
      // divulguer si l'email existe — sauf en cas de 429 rate-limit ou 5xx.
      if (res.status === 429) {
        setError(t('forgot.error_rate_limit'));
        setLoading(false);
        return;
      }
      if (!res.ok) {
        setError(t('forgot.error_server'));
        setLoading(false);
        return;
      }
      setSent(true);
    } catch {
      setError(t('forgot.error_connection'));
    }
    setLoading(false);
  };

  return (
    <>
      <MetaHead page="auth_forgot" />
      <AuroraBackground />
      <style>{css}</style>
      <div className="auth-mini-wrap">
        <header className="auth-mini-nav">
          <Link to="/" className="auth-mini-logo">
            <div className="auth-mini-logo-square">R</div>
            <span className="auth-mini-logo-name">Revyo</span>
          </Link>
          <LanguageSwitcher compact />
        </header>

        <main className="auth-mini-main">
          <div className="auth-mini-card">
            <h1 className="auth-mini-title">{t('forgot.title')}</h1>
            <p className="auth-mini-sub">
              {t('forgot.subtitle')}
            </p>

            {error && <div className="auth-mini-error">{error}</div>}

            {sent ? (
              <div className="auth-mini-success">
                {t('forgot.sent_message')}
              </div>
            ) : (
              <>
                <div className="auth-mini-field">
                  <label htmlFor="forgot-email">{t('forgot.email_label')}</label>
                  <input
                    id="forgot-email"
                    type="email"
                    placeholder={t('forgot.email_placeholder')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && email && handleSubmit()}
                    autoComplete="email"
                  />
                </div>

                <button
                  className="auth-mini-btn"
                  onClick={handleSubmit}
                  disabled={!email || loading}
                >
                  {loading ? t('forgot.submitting') : t('forgot.submit')}
                </button>
              </>
            )}

            <p className="auth-mini-back">
              <span onClick={() => navigate('/login')} className="auth-mini-link">
                {t('common.back_to_login')}
              </span>
            </p>
          </div>
        </main>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════
// CSS — Dark aurora (Phase 2). Carte glass unique centrée.
// ════════════════════════════════════════════════════════════
const css = `
.auth-mini-wrap{position:relative;z-index:1;min-height:100vh;display:flex;flex-direction:column;color:#F5F2EB;font-family:'Inter',system-ui,-apple-system,sans-serif;-webkit-font-smoothing:antialiased;}

/* ══ Nav logo seul ══ */
.auth-mini-nav{display:flex;align-items:center;justify-content:space-between;padding:20px 32px;max-width:1200px;margin:0 auto;width:100%;box-sizing:border-box;}
.auth-mini-logo{display:flex;align-items:center;gap:10px;text-decoration:none;color:#F5F2EB;}
.auth-mini-logo-square{width:36px;height:36px;background:linear-gradient(135deg,#E8502A,#C73E1D);color:#FFF;border-radius:9px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:20px;box-shadow:0 4px 12px -2px rgba(232,80,42,.5);}
.auth-mini-logo-name{font-weight:700;font-size:19px;letter-spacing:-0.3px;color:#F5F2EB;}

/* ══ Main centré ══ */
.auth-mini-main{flex:1;display:flex;align-items:center;justify-content:center;padding:20px;}
.auth-mini-card{background:rgba(255,255,255,.025);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border:1px solid rgba(237,234,227,.14);border-radius:24px;padding:44px;width:100%;max-width:440px;box-shadow:0 40px 80px -20px rgba(0,0,0,.4);box-sizing:border-box;}

.auth-mini-title{font-family:'Sora','Inter',sans-serif;font-size:24px;font-weight:700;letter-spacing:-0.5px;color:#F5F2EB;margin:0 0 8px;}
.auth-mini-sub{font-size:14px;color:rgba(245,242,235,.62);margin:0 0 28px;line-height:1.5;}

.auth-mini-error{background:rgba(232,80,42,.12);color:#FFB199;padding:12px 16px;border-radius:10px;font-size:13px;margin-bottom:16px;border:1px solid rgba(232,80,42,.4);}
.auth-mini-success{background:rgba(46,160,90,.15);color:#7BE0A8;padding:14px 16px;border-radius:10px;font-size:14px;line-height:1.5;border:1px solid rgba(46,160,90,.3);}

.auth-mini-field{margin-bottom:16px;}
.auth-mini-field label{display:block;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.07em;color:rgba(245,242,235,.62);margin-bottom:8px;}
.auth-mini-field input{width:100%;padding:13px 16px;border:1.5px solid rgba(237,234,227,.14);border-radius:10px;font-size:15px;font-family:inherit;color:#F5F2EB;outline:none;transition:border-color .15s,box-shadow .15s,background .15s;background:rgba(255,255,255,.04);box-sizing:border-box;color-scheme:dark;}
.auth-mini-field input::placeholder{color:rgba(245,242,235,.4);}
.auth-mini-field input:focus{border-color:#E8502A;box-shadow:0 0 0 4px rgba(232,80,42,.15);background:rgba(255,255,255,.06);}
.auth-mini-field input:-webkit-autofill,
.auth-mini-field input:-webkit-autofill:hover,
.auth-mini-field input:-webkit-autofill:focus{-webkit-text-fill-color:#F5F2EB;-webkit-box-shadow:0 0 0 1000px rgba(19,17,15,.95) inset;transition:background-color 5000s ease-in-out 0s;caret-color:#F5F2EB;}

.auth-mini-btn{width:100%;padding:15px;background:linear-gradient(135deg,#E8502A 0%,#FF6B3D 100%);color:#FFF;border:none;border-radius:10px;font-size:15px;font-weight:600;cursor:pointer;font-family:inherit;margin-top:8px;transition:all .15s;box-shadow:0 10px 30px -8px rgba(232,80,42,.55),inset 0 1px 0 rgba(255,255,255,.2);}
.auth-mini-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 14px 36px -8px rgba(232,80,42,.65),inset 0 1px 0 rgba(255,255,255,.2);}
.auth-mini-btn:disabled{opacity:.45;cursor:not-allowed;box-shadow:none;transform:none;}

.auth-mini-back{text-align:center;margin:22px 0 0;font-size:14px;color:rgba(245,242,235,.62);}
.auth-mini-link{color:#FF8A3D;cursor:pointer;font-weight:600;text-decoration:none;}
.auth-mini-link:hover{text-decoration:underline;}

@media (max-width:600px){
  .auth-mini-nav{padding:16px 20px;}
  .auth-mini-card{padding:30px 24px;}
  .auth-mini-title{font-size:22px;}
}
`;
